import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { MarkEmailUnread as InquiryIcon } from "@mui/icons-material";
import pinPrompt from "../utils/pinPrompt";
import { emailSenderHeaders } from "../utils/emailSender";

/**
 * "Payment Inquiry" — email a customer asking about the status of an open
 * (usually overdue) invoice, with the invoice PDF attached.
 *
 * The API drafts everything (GET /invoices/{id}/payment-inquiry): the template
 * subject + message, default recipients (the customer's billing / statement
 * contact, plus whoever the invoice itself went to), the customer's other open
 * invoices that can ride along in the same email, earlier inquiries, and the
 * warnings — above all "Bill.com already shows this paid", which needs an
 * explicit "send anyway". The office edits freely; POST sends it as written,
 * with each PDF attached and Bill.com Pay Online links added below.
 *
 * Props: open, invoiceId, onClose(), onSent(result)
 */

const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function dueLabel(days) {
  if (days === null || days === undefined) return "";
  if (days > 0) return `${days} day${days === 1 ? "" : "s"} past due`;
  if (days === 0) return "due today";
  return `due in ${-days} day${days === -1 ? "" : "s"}`;
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "");

// FastAPI's detail is a string, a {message} object, or a validation list.
function errorText(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail.message === "string") return detail.message;
  if (Array.isArray(detail) && detail.length) return detail.map((d) => d.msg || String(d)).join("; ");
  return fallback;
}

function newRequestId() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (e) { /* fall through */ }
  return `pi-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const SEVERITY = { error: "error", warning: "warning", info: "info" };

export default function PaymentInquiryDialog({ open, invoiceId, onClose, onSent }) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [includeIds, setIncludeIds] = useState([]);
  const [toId, setToId] = useState("");
  const [ccIds, setCcIds] = useState([]);
  const [copySelf, setCopySelf] = useState(true);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [edited, setEdited] = useState(false);
  const [ack, setAck] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [openHistoryId, setOpenHistoryId] = useState(null);
  const [showNotYetDue, setShowNotYetDue] = useState(false);
  const requestIdRef = useRef(null);   // idempotency key: one per dialog opening
  const loadSeq = useRef(0);           // ignore a slow draft that a newer one replaced
  const draftRef = useRef(null);       // the draft the text on screen was written for

  // keepRecipients: the office already picked To/CC. keepText: re-read only the
  // facts and warnings (after the send found Bill.com changed) — never throw
  // away what the office wrote.
  const load = useCallback(async (ids, keepRecipients, keepText) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setLoadError(null);
    try {
      const params = ids.length ? { include: ids.join(",") } : {};
      // After a refused send, read Bill.com live — another server's cache may be a minute old.
      if (keepText) params.fresh_bill_com = true;
      const res = await axios.get(`/invoices/${invoiceId}/payment-inquiry`, { params });
      if (seq !== loadSeq.current) return;
      const d = res.data;
      draftRef.current = d;
      setDraft(d);
      setAck(false);
      if (!keepText) {
        setSubject(d.subject || "");
        setBody(d.body || "");
        setEdited(false);
      }
      if (!keepRecipients) {
        setToId(d.default_to_contact_id ?? "");
        setCcIds(d.default_cc_contact_ids || []);
        setCopySelf(d.default_send_copy_to_self !== false);
      }
    } catch (err) {
      if (seq !== loadSeq.current) return;
      // The checkboxes must keep matching the invoices the text was drafted for.
      setIncludeIds(((draftRef.current && draftRef.current.invoices) || []).slice(1).map((i) => i.invoice_id));
      setLoadError(errorText(err, "Couldn't load the inquiry draft."));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (!open || !invoiceId) return;
    requestIdRef.current = newRequestId();
    draftRef.current = null;
    setDraft(null);
    setIncludeIds([]);
    setSendError(null);
    setShowHistory(false);
    setOpenHistoryId(null);
    setShowNotYetDue(false);
    load([], false);
  }, [open, invoiceId, load]);

  const warnings = draft?.warnings || [];
  const needsAck = warnings.some((w) => w.requires_ack);
  const contacts = draft?.contacts || [];
  const candidates = draft?.candidates || [];
  // Overdue invoices (and any already ticked) are the ones worth chasing together;
  // a big customer can have a dozen not-yet-due ones, so those fold away.
  const chaseNow = candidates.filter((c) => c.is_overdue || includeIds.includes(c.invoice_id));
  const notYetDue = candidates.filter((c) => !c.is_overdue && !includeIds.includes(c.invoice_id));
  const history = draft?.history || [];
  const maxInvoices = draft?.max_invoices || 10;
  const invoices = draft?.invoices || [];
  const primary = invoices[0];
  const canSend = !!draft && draft.can_send && !loading && !loadError && !sending && toId !== "" &&
    subject.trim() !== "" && body.trim() !== "" && (!needsAck || ack);

  function toggleInclude(id) {
    if (edited && !window.confirm(
      "Changing which invoices are included rewrites the subject and message from the template. " +
      "Your edits will be lost. Continue?")) return;
    const next = includeIds.includes(id) ? includeIds.filter((x) => x !== id) : [...includeIds, id];
    setIncludeIds(next);
    load(next, true);
  }

  async function send() {
    setSendError(null);
    const pin = await pinPrompt("Enter your PIN to send the payment inquiry:");
    if (!pin) return;
    setSending(true);
    try {
      const res = await axios.post(`/invoices/${invoiceId}/payment-inquiry`, {
        pin,
        to_contact_id: toId,
        cc_contact_ids: ccIds.filter((id) => id !== toId),
        send_copy_to_self: copySelf,
        // exactly the invoices the subject and message were drafted for
        additional_invoice_ids: invoices.slice(1).map((i) => i.invoice_id),
        subject,
        body,
        acknowledged_warnings: warnings.filter((w) => w.level !== "info").map((w) => w.key || w.code),
        client_request_id: requestIdRef.current,
      }, { headers: emailSenderHeaders() });
      if (onSent) onSent(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (!err.response) {
        // Timed out or the connection dropped: the server may still have sent it.
        setSendError("No answer from the server — the email may or may not have gone out. Press Send again: " +
          "if the first attempt went through it will not be sent twice.");
      } else if (detail && detail.code === "unacknowledged_warnings") {
        setSendError(detail.message);
        load(invoices.slice(1).map((i) => i.invoice_id), true, true);   // show the new warning, keep the text
      } else {
        setSendError(errorText(err, "The inquiry was not sent."));
      }
    } finally {
      setSending(false);
    }
  }

  const contactLabel = (c) => `${c.name} <${c.email}>${c.role ? ` — ${c.role}` : ""}`;
  const title = primary
    ? `Payment Inquiry — Invoice #${primary.invoice_number}${draft?.customer?.name ? ` · ${draft.customer.name}` : ""}`
    : "Payment Inquiry";

  return (
    <Dialog open={!!open} onClose={sending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <InquiryIcon color="warning" /> {title}
      </DialogTitle>
      <DialogContent dividers>
        {!draft && loading && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 4, justifyContent: "center" }}>
            <CircularProgress size={24} /> <Typography>Drafting the inquiry and checking Bill.com…</Typography>
          </Box>
        )}
        {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}

        {draft && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* What we're asking about */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Asking about {invoices.length === 1 ? "this invoice" : `${invoices.length} invoices`}
                {invoices.length > 1 && <> — total balance {money(draft.total_balance)}</>}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {invoices.map((inv) => (
                  <Chip
                    key={inv.invoice_id}
                    size="small"
                    color={inv.is_overdue ? "error" : "default"}
                    variant={inv.is_overdue ? "filled" : "outlined"}
                    label={`#${inv.invoice_number} · ${money(inv.balance)} · ${dueLabel(inv.days_past_due) || "no due date"}`}
                  />
                ))}
              </Box>
            </Box>

            {candidates.length > 0 && (
              <Box>
                <Typography variant="subtitle2">
                  Also ask about {draft.customer?.name || "this customer"}'s other open invoices in the same email
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {[...chaseNow, ...(showNotYetDue ? notYetDue : [])].map((c) => {
                    const checked = includeIds.includes(c.invoice_id);
                    const full = !checked && invoices.length >= maxInvoices;
                    return (
                      <FormControlLabel
                        key={c.invoice_id}
                        disabled={loading || sending || full}
                        control={<Checkbox size="small" checked={checked} onChange={() => toggleInclude(c.invoice_id)} />}
                        label={
                          <Typography variant="body2" component="span">
                            #{c.invoice_number} — {money(c.balance)} balance, due {c.due_date || "—"}{" "}
                            <Typography
                              component="span"
                              variant="body2"
                              color={c.is_overdue ? "error.main" : "text.secondary"}
                              sx={{ fontWeight: c.is_overdue ? 600 : 400 }}
                            >
                              ({dueLabel(c.days_past_due) || "no due date"})
                            </Typography>
                          </Typography>
                        }
                      />
                    );
                  })}
                </Box>
                {notYetDue.length > 0 && (
                  <Button size="small" sx={{ alignSelf: "flex-start", px: 0 }} onClick={() => setShowNotYetDue((v) => !v)}>
                    {showNotYetDue
                      ? `Hide the ${notYetDue.length} not yet due`
                      : `Show ${notYetDue.length} more open invoice${notYetDue.length === 1 ? "" : "s"} not yet due`}
                  </Button>
                )}
              </Box>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {warnings.map((w, i) => (
                  <Alert key={`${w.code}-${i}`} severity={SEVERITY[w.level] || "info"} sx={{ py: 0 }}>
                    {w.message}
                  </Alert>
                ))}
                {needsAck && draft.can_send && (
                  <FormControlLabel
                    control={<Checkbox checked={ack} onChange={(e) => setAck(e.target.checked)} />}
                    label="I've checked the warnings above — send the inquiry anyway"
                  />
                )}
              </Box>
            )}

            <Divider />

            {/* Recipients */}
            <FormControl fullWidth size="small">
              <InputLabel>Send to</InputLabel>
              <Select
                label="Send to"
                value={contacts.some((c) => c.contact_id === toId) ? toId : ""}
                onChange={(e) => {
                  const id = e.target.value;
                  setToId(id);
                  setCcIds((prev) => prev.filter((x) => x !== id));
                }}
              >
                {contacts.length === 0 && <MenuItem disabled value=""><em>No contacts with an email address</em></MenuItem>}
                {contacts.map((c) => (
                  <MenuItem key={c.contact_id} value={c.contact_id}>{contactLabel(c)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>CC</InputLabel>
              <Select
                label="CC"
                multiple
                value={ccIds.filter((id) => contacts.some((c) => c.contact_id === id))}
                onChange={(e) => setCcIds(e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const c = contacts.find((x) => x.contact_id === id);
                      return <Chip key={id} size="small" label={c ? c.name : id} />;
                    })}
                  </Box>
                )}
              >
                {contacts.filter((c) => c.contact_id !== toId).map((c) => (
                  <MenuItem key={c.contact_id} value={c.contact_id}>
                    <Checkbox size="small" checked={ccIds.includes(c.contact_id)} />
                    {contactLabel(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              sx={{ mt: -1 }}
              control={<Checkbox checked={copySelf} onChange={(e) => setCopySelf(e.target.checked)} />}
              label={`Send a copy to ${draft.copy_to_self_email} (replies land there too)`}
            />

            {/* The message */}
            <TextField
              label="Subject"
              size="small"
              fullWidth
              value={subject}
              onChange={(e) => { setSubject(e.target.value); setEdited(true); }}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              minRows={12}
              value={body}
              onChange={(e) => { setBody(e.target.value); setEdited(true); }}
              InputProps={{ sx: { fontSize: "0.9rem" } }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Attached: {(draft.attachments || []).join(", ")}.
              {draft.payment_links_expected
                ? " A Bill.com Pay Online link will be added below your message."
                : " No Bill.com Pay Online link will be added."}
              {loading && " Updating…"}
            </Typography>

            {/* Earlier inquiries */}
            {history.length > 0 && (
              <Box>
                <Button size="small" onClick={() => setShowHistory((v) => !v)}>
                  {showHistory ? "Hide" : "Show"} earlier inquiries ({history.length})
                </Button>
                <Collapse in={showHistory}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                    {history.map((h) => (
                      <Box key={h.inquiry_id} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
                        <Typography variant="body2">
                          <strong>{fmtDateTime(h.sent_at)}</strong>{h.sent_by ? ` by ${h.sent_by}` : ""} — to {h.to_email}
                          {h.cc_emails?.length ? ` (cc ${h.cc_emails.join(", ")})` : ""}
                          {h.invoice_numbers?.length > 1 ? ` — invoices ${h.invoice_numbers.map((n) => `#${n}`).join(", ")}` : ""}
                        </Typography>
                        <Button size="small" onClick={() => setOpenHistoryId(openHistoryId === h.inquiry_id ? null : h.inquiry_id)}>
                          {openHistoryId === h.inquiry_id ? "Hide message" : "View message"}
                        </Button>
                        <Collapse in={openHistoryId === h.inquiry_id}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{h.subject}</Typography>
                          <Box component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: "0.85rem", m: 0 }}>
                            {h.body_text}
                          </Box>
                        </Collapse>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )}

            {sendError && <Alert severity="error">{sendError}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {primary?.last_emailed_at && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: "auto", ml: 1 }}>
            Invoice originally emailed {fmtDate(primary.last_emailed_at)}
          </Typography>
        )}
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <InquiryIcon />}
          onClick={send}
          disabled={!canSend}
        >
          {sending ? "Sending…" : "Send Inquiry"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
